import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import {
  useGetTreatyReinsurersAndList,
  useUpdateTreatyReinsurers
} from '@common/slices';

import { cloneDeep } from 'lodash';
import { IBaseDrawerProps, IReinsurerValues } from '@common/types';
import { updateReinsurersDrawerSections } from '@common/constants';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

interface Props extends IBaseDrawerProps {
  relatedCompany: string;
  treatyId: string;
}

const UpdateReinsurersDrawer: React.FC<Props> = ({
  onSuccess,
  relatedCompany,
  treatyId
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [values, setValues] = useState<IReinsurerValues[]>([]);
  const [getCombinedDataLazy] = useGetTreatyReinsurersAndList.useLazy();
  const [updateTreatyReinsurers] = useUpdateTreatyReinsurers();

  const reinsurersListRef = React.useRef<Record<string, string>>({});
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    updateReinsurersDrawerSections(
      values,
      {},
      Object.keys(reinsurersListRef.current).length
    )
  );
  const getTreatyAndReinsurers = useCallback(async () => {
    try {
      const result = await getCombinedDataLazy({
        variables: {
          treatyId: treatyId,
          currentPage: 1,
          currentPageSize: 1000,
          companies: [relatedCompany],
          statuses: ['Active'],
          isLocal: null,
          createdDateFrom: null,
          createdDateTo: null,
          Attribute: 'createdOn',
          Descending: true,
          keywordSearch: ''
        }
      });

      const reinsurers = result.data.reinsurance.searchReinsurers?.items ?? [];
      const treaties = result.data.reinsurance.getTreatyReinsurers ?? [];

      // set reinsurer options
      const reinsurerOptions = lookupListAsRecordObject(
        reinsurers as any,
        false,
        'id',
        'fullName'
      );
      reinsurersListRef.current = reinsurerOptions;

      // build values from treaties
      const newValues: IReinsurerValues[] = treaties.map((item) => {
        // Add to reinsurersList if data is available
        if (item.reinsurerID && item.reinsurer?.fullName) {
          reinsurersListRef.current[item.reinsurerID] = item.reinsurer.fullName;
        }

        // Return the mapped reinsurer value
        return {
          isLeader: item.isLeader,
          reinsurer: item.reinsurerID,
          share: item.sharePercentage
        };
      });

      newValues.sort((a) => (a.isLeader ? -1 : 1));

      setValues(newValues);
      setInputsForm(
        cloneDeep(
          updateReinsurersDrawerSections(
            newValues,
            reinsurersListRef.current,
            Object.keys(reinsurersListRef.current).length
          )
        )
      );
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    }
  }, [getCombinedDataLazy, treatyId]);

  const handleChange = (
    fieldName: any,
    newValue: any,
    sectionIndex: number,
    allValues: any,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
    }
  ) => {
    const { reinsurersRepeater } = allValues;
    setValues(reinsurersRepeater);

    setInputsForm(
      updateReinsurersDrawerSections(
        reinsurersRepeater,
        reinsurersListRef.current,
        Object.keys(reinsurersListRef.current).length
      )
    );
  };

  const initialize = useCallback(async () => {
    getTreatyAndReinsurers();
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleSubmit = async () => {
    const reinsurers = values.map((item, index) => ({
      isLeader: index === 0,
      reinsurerID: item.reinsurer,
      reinsurerShare: Number(item.share)
    }));

    const res = await updateTreatyReinsurers({
      treatyId: treatyId,
      reinsurers
    });

    if (!res.errors) {
      onSuccess();
    }
  };

  return (
    <DynamicFormWidget
      sections={inputsForm}
      onChange={handleChange}
      onSubmit={handleSubmit}
      submitButtonState={submitButtonState}
    />
  );
};

export { UpdateReinsurersDrawer };
